import { RudderAnalytics } from '@rudderstack/analytics-js/bundled';
import { guestbookSites, environment } from '../settings/IntegrationConstants';

const rudderAnalytics = new RudderAnalytics();

const apiKey = guestbookSites[environment]?.rudderstack?.api_key;
// Per-environment data plane URL. Production routes through our own domain
// (see IntegrationConstants) so event delivery survives booking-engine CSPs
// that allowlist *.theguestbook.com but not rudderstack.com.
const dataPlaneUrl = guestbookSites[environment]?.rudderstack?.data_plane_url;
// Optional control-plane override. load() fetches sourceConfig from
// api.rudderstack.com before it will send anything, and the same booking-engine
// CSPs block that host -- when the fetch fails the SDK never finishes
// initialising, so events queue and never flush. Setting config_url points that
// fetch at our own proxied domain too. Left unset until the proxy behaviour
// exists for an environment: an unreachable configUrl breaks the SDK
// everywhere, not just on the engines we're trying to fix.
const configUrl = guestbookSites[environment]?.rudderstack?.config_url;
if (!apiKey) {
  console.error('Rudderstack API key missing for environment:', environment);
} else if (!dataPlaneUrl) {
  console.error('Rudderstack data plane URL missing for environment:', environment);
} else {
  rudderAnalytics.load(apiKey, dataPlaneUrl, configUrl ? { configUrl } : undefined);
}

const safeRudderAnalytics = new Proxy(rudderAnalytics, {
  get(target, prop) {
    if (typeof target[prop] === 'function') {
      return (...args) => {
        try {
          return target[prop](...args);
        } catch (err) {
          console.error(`RudderAnalytics error calling ${prop}:`, err);
        }
      };
    } else {
      return target[prop];
    }
  },
});

// Bind this browser to the signed-in member.
//
// A server-side identify carries no anonymousId, so it establishes an identity
// but attaches no browser session to it -- the dashboard's client-side track
// calls would stay anonymous. Identifying here is what actually associates this
// browser's anonymousId with the member, because the SDK sends its own
// anonymousId along with the call.
//
// window.gb_analytics_id is rendered by the member dashboard layout and only on
// authenticated pages, so this is a no-op everywhere else -- including in the
// BEI bundle, which imports this module but runs on the hotel's own domain
// where the global is never set. Safe to render into that page precisely
// because the page is already behind a session.
//
// Lives here rather than in a component: the dashboard has 15 separate React
// entrypoints (entrypoints/member_dashboards/*), and this module is the one
// thing every one of them imports.
if (typeof window !== 'undefined' && window.gb_analytics_id) {
  safeRudderAnalytics.identify(window.gb_analytics_id);
}

export default safeRudderAnalytics;
